import React from 'react';
import { motion } from 'framer-motion';
import styles from './TabBar.module.css';

export interface TabItem<T extends string = string> {
  id: T;
  label: string;
  icon: React.ElementType;
}

interface TabBarProps<T extends string = string> {
  tabs: TabItem<T>[];
  activeTab: T;
  onChange: (tab: T) => void;
  layoutId?: string;
  className?: string;
  variant?: 'pill' | 'underline';
}

export function TabBar<T extends string>({
  tabs,
  activeTab,
  onChange,
  layoutId = 'tab-pill',
  className = '',
  variant = 'pill',
}: TabBarProps<T>) {
  if (variant === 'underline') {
    return (
      <div className={[styles['bar-underline'], className].filter(Boolean).join(' ')}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onChange(tab.id)}
              className={[styles['tab-underline'], isActive ? styles['tab-underline-active'] : ''].filter(Boolean).join(' ')}
            >
              {isActive && (
                <motion.span
                  layoutId={layoutId + '-underline'}
                  className={styles['underline-indicator']}
                  transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                />
              )}
              <Icon className={styles.icon} />
              <span className={styles['label-underline']}>{tab.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className={[styles.bar, className].filter(Boolean).join(' ')}>
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            className={[styles.tab, isActive ? styles['tab-active'] : ''].filter(Boolean).join(' ')}
            title={tab.label}
            aria-label={tab.label}
          >
            {isActive && (
              <motion.span
                layoutId={layoutId}
                className={styles.pill}
                transition={{ type: 'spring', stiffness: 500, damping: 40 }}
              />
            )}
            <Icon className={styles.icon} />
            <span className={styles.label}>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}
